import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "./baseApi";
import {
  ApiResponse,
  PurchaseOrder,
  PurchaseOrderFilters,
  PurchaseOrderReceipt,
  PurchaseOrderStats,
  CreatePurchaseOrderData,
  UpdatePurchaseOrderData,
  ReceivePurchaseOrderData,
  CancelPurchaseOrderData,
  PurchaseReturn,
  RecordPaymentData,
  PurchaseOrderPayment,
  CreateReturnData,
} from "@/types";

export const purchaseOrderApi = createApi({
  reducerPath: "purchaseOrderApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["PurchaseOrder", "PurchaseOrderReceipt", "PurchaseOrderStats", "PurchaseReturn", "POPayment"],
  endpoints: builder => ({
    getPurchaseOrders: builder.query<
      ApiResponse<{ data: PurchaseOrder[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>,
      PurchaseOrderFilters | void
    >({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              searchParams.append(key, value.toString());
            }
          });
        }
        const qs = searchParams.toString();
        return qs ? `/purchase-orders?${qs}` : "/purchase-orders";
      },
      providesTags: result =>
        result?.data?.data
          ? [
              ...result.data.data.map(({ id }) => ({ type: "PurchaseOrder" as const, id })),
              { type: "PurchaseOrder", id: "LIST" },
            ]
          : [{ type: "PurchaseOrder", id: "LIST" }],
    }),

    getPurchaseOrderStats: builder.query<
      ApiResponse<PurchaseOrderStats>,
      { storeId: string; outletId?: string }
    >({
      query: ({ storeId, outletId }) => {
        const params = new URLSearchParams({ storeId });
        if (outletId) params.append("outletId", outletId);
        return `/purchase-orders/stats?${params.toString()}`;
      },
      providesTags: [{ type: "PurchaseOrderStats", id: "STATS" }],
    }),

    getPurchaseOrder: builder.query<ApiResponse<PurchaseOrder>, string>({
      query: id => `/purchase-orders/${id}`,
      providesTags: (_result, _error, id) => [{ type: "PurchaseOrder", id }],
    }),

    getReceipts: builder.query<ApiResponse<PurchaseOrderReceipt[]>, string>({
      query: id => `/purchase-orders/${id}/receipts`,
      providesTags: (_result, _error, id) => [{ type: "PurchaseOrderReceipt", id }],
    }),

    getLastUnitCost: builder.query<
      ApiResponse<{ unitCost: number | null }>,
      { storeId: string; itemId: string }
    >({
      query: ({ storeId, itemId }) =>
        `/purchase-orders/last-unit-cost?storeId=${storeId}&itemId=${itemId}`,
    }),

    createPurchaseOrder: builder.mutation<ApiResponse<PurchaseOrder>, CreatePurchaseOrderData>({
      query: data => ({
        url: "/purchase-orders",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        { type: "PurchaseOrder", id: "LIST" },
        { type: "PurchaseOrderStats", id: "STATS" },
      ],
    }),

    updatePurchaseOrder: builder.mutation<
      ApiResponse<PurchaseOrder>,
      { id: string; data: UpdatePurchaseOrderData }
    >({
      query: ({ id, data }) => ({
        url: `/purchase-orders/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "PurchaseOrder", id },
        { type: "PurchaseOrder", id: "LIST" },
      ],
    }),

    deletePurchaseOrder: builder.mutation<ApiResponse<{ id: string }>, string>({
      query: id => ({
        url: `/purchase-orders/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "PurchaseOrder", id },
        { type: "PurchaseOrder", id: "LIST" },
        { type: "PurchaseOrderStats", id: "STATS" },
      ],
    }),

    approvePurchaseOrder: builder.mutation<ApiResponse<PurchaseOrder>, string>({
      query: id => ({
        url: `/purchase-orders/${id}/approve`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "PurchaseOrder", id },
        { type: "PurchaseOrder", id: "LIST" },
        { type: "PurchaseOrderStats", id: "STATS" },
      ],
    }),

    orderPurchaseOrder: builder.mutation<ApiResponse<PurchaseOrder>, string>({
      query: id => ({
        url: `/purchase-orders/${id}/order`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "PurchaseOrder", id },
        { type: "PurchaseOrder", id: "LIST" },
        { type: "PurchaseOrderStats", id: "STATS" },
      ],
    }),

    cancelPurchaseOrder: builder.mutation<
      ApiResponse<PurchaseOrder>,
      { id: string; data?: CancelPurchaseOrderData }
    >({
      query: ({ id, data }) => ({
        url: `/purchase-orders/${id}/cancel`,
        method: "PATCH",
        body: data || {},
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "PurchaseOrder", id },
        { type: "PurchaseOrder", id: "LIST" },
        { type: "PurchaseOrderStats", id: "STATS" },
      ],
    }),

    closePurchaseOrder: builder.mutation<ApiResponse<PurchaseOrder>, string>({
      query: id => ({
        url: `/purchase-orders/${id}/close`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "PurchaseOrder", id },
        { type: "PurchaseOrder", id: "LIST" },
        { type: "PurchaseOrderStats", id: "STATS" },
      ],
    }),

    receiveGoods: builder.mutation<
      ApiResponse<{ receipt: PurchaseOrderReceipt; updatedPO: PurchaseOrder }>,
      { id: string; data: ReceivePurchaseOrderData }
    >({
      query: ({ id, data }) => ({
        url: `/purchase-orders/${id}/receive`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "PurchaseOrder", id },
        { type: "PurchaseOrder", id: "LIST" },
        { type: "PurchaseOrderReceipt", id },
        { type: "PurchaseOrderStats", id: "STATS" },
      ],
    }),

    recordPayment: builder.mutation<
      ApiResponse<PurchaseOrder>,
      { id: string; data: RecordPaymentData }
    >({
      query: ({ id, data }) => ({
        url: `/purchase-orders/${id}/payment`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "PurchaseOrder", id },
        { type: "PurchaseOrder", id: "LIST" },
        { type: "POPayment", id },
        { type: "PurchaseOrderStats", id: "STATS" },
      ],
    }),

    getPaymentHistory: builder.query<ApiResponse<PurchaseOrderPayment[]>, string>({
      query: id => `/purchase-orders/${id}/payments`,
      providesTags: (_result, _error, id) => [{ type: "POPayment", id }],
    }),

    getReturns: builder.query<ApiResponse<PurchaseReturn[]>, string>({
      query: id => `/purchase-orders/${id}/returns`,
      providesTags: (_result, _error, id) => [{ type: "PurchaseReturn", id }],
    }),

    createReturn: builder.mutation<
      ApiResponse<PurchaseReturn>,
      { id: string; data: CreateReturnData }
    >({
      query: ({ id, data }) => ({
        url: `/purchase-orders/${id}/returns`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "PurchaseReturn", id },
        { type: "PurchaseOrder", id },
        { type: "PurchaseOrder", id: "LIST" },
      ],
    }),

    approveReturn: builder.mutation<
      ApiResponse<PurchaseReturn>,
      { poId: string; returnId: string }
    >({
      query: ({ poId, returnId }) => ({
        url: `/purchase-orders/${poId}/returns/${returnId}/approve`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { poId }) => [
        { type: "PurchaseReturn", id: poId },
        { type: "PurchaseOrder", id: poId },
        { type: "PurchaseOrder", id: "LIST" },
      ],
    }),

    cancelReturn: builder.mutation<
      ApiResponse<PurchaseReturn>,
      { poId: string; returnId: string }
    >({
      query: ({ poId, returnId }) => ({
        url: `/purchase-orders/${poId}/returns/${returnId}/cancel`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { poId }) => [
        { type: "PurchaseReturn", id: poId },
        { type: "PurchaseOrder", id: poId },
        { type: "PurchaseOrder", id: "LIST" },
      ],
    }),

    batchCreatePurchaseOrders: builder.mutation<
      ApiResponse<{
        created: Array<{ poNumber: string; distributorName: string; itemCount: number }>;
        errors: Array<{ distributorName: string; message: string }>;
      }>,
      {
        storeId: string;
        outletId: string;
        groups: Array<{
          distributorId: string;
          distributorName: string;
          items: Array<{ itemId: string; orderedQuantity: number; unitCost: number; expiryDate?: string; notes?: string }>;
        }>;
      }
    >({
      query: (data) => ({
        url: "/purchase-orders/batch-create",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        { type: "PurchaseOrder", id: "LIST" },
        { type: "PurchaseOrderStats", id: "STATS" },
      ],
    }),
  }),
});

export const {
  useGetLastUnitCostQuery,
  useLazyGetLastUnitCostQuery,
  useGetPurchaseOrdersQuery,
  useGetPurchaseOrderStatsQuery,
  useGetPurchaseOrderQuery,
  useGetReceiptsQuery,
  useCreatePurchaseOrderMutation,
  useUpdatePurchaseOrderMutation,
  useDeletePurchaseOrderMutation,
  useApprovePurchaseOrderMutation,
  useOrderPurchaseOrderMutation,
  useCancelPurchaseOrderMutation,
  useClosePurchaseOrderMutation,
  useReceiveGoodsMutation,
  useRecordPaymentMutation,
  useGetReturnsQuery,
  useCreateReturnMutation,
  useApproveReturnMutation,
  useCancelReturnMutation,
  useGetPaymentHistoryQuery,
  useBatchCreatePurchaseOrdersMutation,
} = purchaseOrderApi;
